import { useEffect, useRef, useState } from 'react';
import { CalendarCheck, Facebook, Instagram, Linkedin, Menu, Star, Twitter, Youtube, X } from 'lucide-react';
import { formatCents } from '@smup/availability';
import { API_URL, type Landing, type LandingPhoto, type LandingReview, type Site, type SocialLink } from './BookingPage';

/*
 * The three landing layouts (issue #89).
 *
 * ⚠️ LAYOUT ONLY. Every colour here comes from `site.branding` as an inline style, exactly
 * as the booking flow does. A template with its own palette would fight the accent the
 * tenant already chose for their booking page, and the two sit on the same screen.
 *
 * ⚠️ ONE CONTENT MODEL. All three read the same fields, so switching template rearranges
 * the page and never strands copy someone wrote.
 *
 * Services are not listed here. They are rendered by the booking flow below the fold from
 * `booking_services` — describing them twice guarantees the two descriptions disagree.
 */

export const photoUrl = (p: LandingPhoto) => `${API_URL}${p.url}`;

/**
 * Scroll to an anchor on this page.
 *
 * ⚠️ PROGRAMMATIC, NOT `scroll-behavior: smooth` IN CSS. The CSS property animates for
 * everyone including people who have asked the system for less motion; this checks. Same
 * reason BookCta has always done it this way — nav links now share the rule instead of
 * jumping while the button glides.
 */
export function scrollToId(e: React.MouseEvent, id: string) {
  const target = document.getElementById(id);
  if (!target) return; // a section that is not rendered (no add-ons, say) keeps the default jump
  e.preventDefault();
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  history.replaceState(null, '', `#${id}`);
}

/** The CTA. Scrolls rather than navigates — the booking flow is on this page. */
function BookCta({ accent, onAccent, compact = false }: { accent: string; onAccent: string; compact?: boolean }) {
  return (
    <a
      href="#book"
      onClick={(e) => {
        e.preventDefault();
        /*
         * Programmatic so the scroll can be smooth AND respect prefers-reduced-motion.
         * `scroll-behavior: smooth` in CSS would animate for everyone, including people who
         * have asked the system not to.
         */
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        document.getElementById('book')?.scrollIntoView({
          behavior: reduce ? 'auto' : 'smooth',
          block: 'start',
        });
        // Move the URL on so the position is shareable and Back returns to the top.
        history.replaceState(null, '', '#book');
      }}
      className={`inline-flex shrink-0 items-center gap-2 rounded-xl font-semibold shadow-sm transition-transform hover:scale-[1.02] motion-reduce:transition-none motion-reduce:hover:scale-100 ${
        compact ? 'px-4 py-2 text-sm' : 'px-6 py-3.5 text-base'
      }`}
      style={{ backgroundColor: accent, color: onAccent }}
    >
      <CalendarCheck className={compact ? 'h-4 w-4' : 'h-5 w-5'} />
      {/*
        ⚠️ THE HEADER BUTTON SHORTENS ON A PHONE; the hero one never does. In the masthead
        this sits beside the business name and is `shrink-0`, so the full label does not get
        smaller — the NAME wraps instead, and "Fluffy Paw Spa" broke across two lines on a
        375px screen while the bar grew to fit it. "Book" is unambiguous next to a calendar
        icon in a sticky bar; the hero button has the room to say the whole thing and is the
        one doing the persuading.
      */}
      {compact ? (
        <>
          <span className="sm:hidden">Book</span>
          <span className="hidden sm:inline">Book an appointment</span>
        </>
      ) : (
        'Book an appointment'
      )}
    </a>
  );
}

/** Multi-paragraph plain text. Line breaks are the author's and are preserved. */
function About({ text }: { text: string | null }) {
  if (!text?.trim()) return null;
  return <p className="whitespace-pre-line text-lg leading-relaxed opacity-80">{text}</p>;
}

/**
 * A photograph the operator cropped, drawn into a frame she never saw (#292).
 *
 * ⚠️ THE CROP AND THE FOCAL POINT ARE BOTH APPLIED HERE, IN THAT ORDER, and the order is the
 * whole design. The crop says what the photograph IS — she drew that box against her own
 * photo and it is one rectangle. The point says what survives when that rectangle meets a
 * frame of a different shape, and the frames are three different shapes (0037). Doing only
 * the crop would put her rectangle in a hero that is nothing like it and cut her subject out
 * again, which is the problem the crop was supposed to solve.
 *
 * ⚠️ PURE CSS, NO MEASUREMENT, and that is worth the two nested elements. The obvious
 * implementation reads the frame's pixel size and computes a scale, which means a ref, a
 * resize observer and a first paint at the wrong size — on a page whose hero is sized in
 * `vh` and changes at every breakpoint. This does the same arithmetic in the layout engine:
 *
 *   - The middle box carries the CROP's aspect ratio and `min-width/min-height: 100%`, which
 *     makes it the smallest box of that shape that still covers the frame. That is exactly
 *     what `object-fit: cover` means, expressed as a box.
 *   - The image inside is sized as a percentage of that box — `width: Nw/cw`, `height: Nh/ch`
 *     — so the crop rectangle lands on the box precisely. Because the box already has the
 *     crop's aspect, those two percentages are the same scale, so nothing is stretched.
 *   - The box is then positioned by the focal point using object-position's own formula:
 *     put the point `f` along the content at the point `f` along the frame.
 *
 * ⚠️ `maxWidth: none` IS LOAD-BEARING. Tailwind's preflight sets `img { max-width: 100% }`,
 * which silently clamps the oversized inner image and un-does the crop.
 */
function CroppedPhoto({
  photo, crop, className,
}: {
  photo: LandingPhoto;
  crop: { x: number; y: number; w: number; h: number };
  className: string;
}) {
  const fx = photo.focalX ?? 50;
  const fy = photo.focalY ?? 50;
  return (
    <div className={`relative overflow-hidden ${className}`}>
      <div
        style={{
          position: 'absolute',
          left: `${fx}%`,
          top: `${fy}%`,
          transform: `translate(-${fx}%, -${fy}%)`,
          minWidth: '100%',
          minHeight: '100%',
          aspectRatio: `${crop.w} / ${crop.h}`,
        }}
      >
        <img
          src={photoUrl(photo)}
          alt={photo.caption ?? ''}
          loading="lazy"
          style={{
            position: 'absolute',
            width: `${(photo.width / crop.w) * 100}%`,
            height: `${(photo.height / crop.h) * 100}%`,
            left: `${(-crop.x / crop.w) * 100}%`,
            top: `${(-crop.y / crop.h) * 100}%`,
            maxWidth: 'none',
          }}
        />
      </div>
    </div>
  );
}

export function Photo({ photo, className = '' }: { photo: LandingPhoto; className?: string }) {
  /*
   * The uncropped path below is byte-for-byte what this component was before #292, and it is
   * kept rather than folded into the cropped one so that every photo uploaded before the
   * crop dialog existed renders through exactly the code that has been serving it. A crop is
   * opt-in; nobody's live page changes until they draw a box.
   */
  if (photo.crop) return <CroppedPhoto photo={photo} crop={photo.crop} className={className} />;
  return (
    <img
      src={photoUrl(photo)}
      alt={photo.caption ?? ''}
      width={photo.width}
      height={photo.height}
      /* Dimensions are known, so the browser reserves the space and the page does not jump
         as photos arrive — the CTA staying put matters more here than anywhere else. */
      loading="lazy"
      /*
       * ⚠️ THE FOCAL POINT IS APPLIED HERE, IN THE ONE PLACE EVERY TEMPLATE RENDERS A PHOTO
       * (0037) — including the bespoke Fluffy Paw layout, which is why that layout gets this
       * for free and cannot drift away from the others.
       *
       * `object-cover` crops whatever does not fit, and it cropped from the centre for every
       * photo on every page until this existed. A 3:2 photo in the hero's 2.67:1 frame loses
       * about a quarter of its height, and if the subject sits low that is the subject. The
       * operator picks the point that must survive; the CSS keeps it in frame at every
       * breakpoint, which a stored crop rectangle could not do.
       *
       * Defaults to dead centre when absent, which is exactly what this did before — an older
       * payload renders identically rather than jumping.
       */
      style={{ objectPosition: `${photo.focalX ?? 50}% ${photo.focalY ?? 50}%` }}
      className={`object-cover ${className}`}
    />
  );
}

export function LandingHero({ site, landing, onAccent }: TemplateProps) {
  const { accentColor } = site.branding;
  const hero = landing.photos[0];
  const rest = landing.photos.slice(1, 4);
  return (
    <div className="mx-auto max-w-5xl px-5 py-10 sm:py-16">
      {hero && (
        <Photo photo={hero} className="mb-10 h-[38vh] max-h-[420px] w-full rounded-2xl sm:h-[46vh]" />
      )}
      <div className="max-w-2xl">
        <Headline landing={landing} site={site} />
        <div className="mt-6 space-y-5">
          <About text={landing.about} />
          <BookCta accent={accentColor} onAccent={onAccent} />
        </div>
      </div>
      {rest.length > 0 && (
        <div className="mt-12 grid gap-4 sm:grid-cols-3">
          {rest.map((p) => (
            <Photo key={p.id} photo={p} className="h-44 w-full rounded-xl" />
          ))}
        </div>
      )}
      <Reviews reviews={landing.reviews} bg={site.branding.backgroundColor} />
      <Social links={landing.social} />
    </div>
  );
}

export function LandingSplit({ site, landing, onAccent }: TemplateProps) {
  const { accentColor } = site.branding;
  const hero = landing.photos[0];
  const rest = landing.photos.slice(1, 5);
  return (
    <div className="mx-auto max-w-6xl px-5 py-10 sm:py-16">
      <div className="grid items-center gap-10 md:grid-cols-2">
        <div>
          <Headline landing={landing} site={site} />
          <div className="mt-6 space-y-5">
            <About text={landing.about} />
            <BookCta accent={accentColor} onAccent={onAccent} />
          </div>
        </div>
        {hero && <Photo photo={hero} className="h-[46vh] max-h-[520px] w-full rounded-2xl" />}
      </div>
      {rest.length > 0 && (
        <div className="mt-12 grid gap-4 sm:grid-cols-4">
          {rest.map((p) => (
            <Photo key={p.id} photo={p} className="h-36 w-full rounded-xl" />
          ))}
        </div>
      )}
      <Reviews reviews={landing.reviews} bg={site.branding.backgroundColor} />
      <Social links={landing.social} />
    </div>
  );
}

export function LandingGallery({ site, landing, onAccent }: TemplateProps) {
  const { accentColor } = site.branding;
  return (
    <div className="mx-auto max-w-6xl px-5 py-10 sm:py-16">
      <div className="mx-auto max-w-2xl text-center">
        <Headline landing={landing} site={site} centered />
        <div className="mt-6 space-y-6">
          <About text={landing.about} />
          <BookCta accent={accentColor} onAccent={onAccent} />
        </div>
      </div>
      {landing.photos.length > 0 && (
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {landing.photos.map((p, i) => (
            <figure key={p.id}>
              {/* The first photo takes two columns so the grid has a focal point rather than
                  reading as a contact sheet. */}
              <Photo
                photo={p}
                className={`w-full rounded-xl ${i === 0 ? 'h-64 sm:col-span-2 lg:col-span-2' : 'h-56'}`}
              />
              {p.caption && <figcaption className="mt-2 text-sm opacity-70">{p.caption}</figcaption>}
            </figure>
          ))}
        </div>
      )}
      <Reviews reviews={landing.reviews} bg={site.branding.backgroundColor} centered />
      <Social links={landing.social} centered />
    </div>
  );
}

/**
 * Testimonials.
 *
 * ⚠️ ATTRIBUTED, NEVER AGGREGATED. These are quotes the operator typed in. The section is
 * headed "What customers say" rather than "Reviews", carries no average, no count and no
 * platform logo, and stars appear only on a quote whose author actually gave one — because
 * a self-entered score rendered as a summary is indistinguishable to a reader from verified
 * third-party data.
 */
export function Reviews({
  reviews,
  bg,
  centered = false,
}: {
  reviews: LandingReview[];
  /** The tenant's page background, so the card tint can sit on it either way. */
  bg: string;
  centered?: boolean;
}) {
  if (reviews.length === 0) return null;
  /*
   * ⚠️ The lift has to follow the background. A black tint is invisible on a dark page and a
   * white one is invisible on a light page — the booking flow's Card solves this the same
   * way, and these sit on the same screen.
   */
  const tint = isDark(bg) ? 'rgb(255 255 255 / 0.08)' : 'rgb(0 0 0 / 0.04)';
  return (
    <section className="mt-14">
      <h2 className={`text-xs font-bold uppercase tracking-[0.14em] opacity-60 ${centered ? 'text-center' : ''}`}>
        What customers say
      </h2>
      <div
        className={`mt-5 grid gap-5 ${
          reviews.length === 1 ? 'max-w-2xl' : 'sm:grid-cols-2 lg:grid-cols-3'
        } ${centered && reviews.length === 1 ? 'mx-auto' : ''}`}
      >
        {reviews.map((r) => (
          <figure key={r.id} className="rounded-2xl p-5" style={{ backgroundColor: tint }}>
            {r.rating != null && (
              <div className="mb-2 flex gap-0.5" aria-label={`${r.rating} out of 5`}>
                {Array.from({ length: 5 }, (_, i) => (
                  <Star
                    key={i}
                    className="h-4 w-4"
                    aria-hidden="true"
                    fill={i < r.rating! ? 'currentColor' : 'none'}
                    style={{ opacity: i < r.rating! ? 0.8 : 0.25 }}
                  />
                ))}
              </div>
            )}
            <blockquote className="whitespace-pre-line text-[0.95rem] leading-relaxed opacity-90">
              “{r.quote}”
            </blockquote>
            {(r.author || r.source) && (
              <figcaption className="mt-3 text-sm opacity-60">
                {r.author}
                {r.author && r.source ? ' · ' : ''}
                {r.source}
              </figcaption>
            )}
          </figure>
        ))}
      </div>
    </section>
  );
}

export interface TemplateProps {
  site: Site;
  landing: Landing;
  /** Readable foreground for the accent colour, computed once by the caller. */
  onAccent: string;
  /**
   * Send the customer into the booking flow with this service already chosen.
   *
   * ⚠️ OPTIONAL, because only the pages that offer a per-service CTA need it. A template
   * that just scrolls to the form leaves it out and keeps using `BookCta`.
   */
  onPickService?: (serviceId: string) => void;
}

function Headline({
  landing,
  site,
  centered = false,
}: {
  landing: Landing;
  site: Site;
  centered?: boolean;
}) {
  return (
    <div className={centered ? 'text-center' : undefined}>
      {landing.tagline && (
        <p className="mb-3 text-xs font-bold uppercase tracking-[0.14em] opacity-60">
          {landing.tagline}
        </p>
      )}
      {/* Falls back to the business name: a tenant who uploads photos but writes no headline
          gets their name in the hero, not an empty space where a sentence should be. */}
      <h1 className="text-5xl font-bold leading-[0.98] tracking-[-0.03em] sm:text-7xl lg:text-8xl">
        {landing.headline?.trim() || site.business.name}
      </h1>
    </div>
  );
}

/** Same relative-luminance rule the booking flow uses, so the two agree on "dark". */
function isDark(hex: string): boolean {
  const n = hex.replace('#', '');
  const full = n.length === 3 ? n.split('').map((c) => c + c).join('') : n;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16) / 255) as [number, number, number];
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b) < 0.5;
}

/**
 * Where else the business exists online.
 *
 * ⚠️ ONLY THE PLATFORMS WITH A REAL ICON GET ONE. lucide ships Facebook, Instagram, YouTube,
 * LinkedIn and X; it has no TikTok, Yelp, Nextdoor or Google Business mark. Drawing an
 * approximation of a trademarked logo from memory produces something that is both wrong and
 * visibly wrong, so those render as a named pill instead. A reader loses nothing — the name
 * is the label either way — and nobody is shipped a bad imitation of somebody's brand.
 */
const ICONS: Partial<Record<SocialLink['platform'], typeof Facebook>> = {
  facebook: Facebook,
  instagram: Instagram,
  youtube: Youtube,
  linkedin: Linkedin,
  x: Twitter,
};

const LABELS: Record<SocialLink['platform'], string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  x: 'X',
  linkedin: 'LinkedIn',
  google: 'Google',
  yelp: 'Yelp',
  nextdoor: 'Nextdoor',
};

export function Social({ links, centered = false }: { links: SocialLink[]; centered?: boolean }) {
  if (links.length === 0) return null;
  return (
    <div className={`mt-10 flex flex-wrap items-center gap-2 ${centered ? 'justify-center' : ''}`}>
      {links.map((l) => {
        const Icon = ICONS[l.platform];
        return (
          <a
            key={l.platform}
            href={l.url}
            target="_blank"
            /* noopener: these are links the operator typed, to sites we do not control. */
            rel="noreferrer noopener"
            aria-label={LABELS[l.platform]}
            title={LABELS[l.platform]}
            className="flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium opacity-70 transition-opacity hover:opacity-100"
            style={{ backgroundColor: 'rgb(127 127 127 / 0.12)' }}
          >
            {Icon ? <Icon className="h-4 w-4" /> : <span>{LABELS[l.platform]}</span>}
          </a>
        );
      })}
    </div>
  );
}

/**
 * ⚠️ A ONE-OFF PAGE FOR FLUFFY PAW SPA. Not a template, despite living beside three of them.
 *
 * Every other entry in TEMPLATES describes a SHAPE — where the photo goes — and works for any
 * tenant. This one is a bespoke page built to an approved design for one customer: her
 * sections, her running order, her words. A second groomer does NOT reuse it; they get their
 * own component, and that is the decision (Mike, 2026-08-13) — one-offs are cheap at this
 * volume and a generalised page nobody asked for is not.
 *
 * ⚠️ NOT IN `TEMPLATES`, and not a selectable value. It is chosen by tenant slug in App.tsx,
 * so it cannot be handed to another business from the picker. The platform ships three
 * templates; this is paid bespoke work sitting beside them.
 *
 * ⚠️ PRICES COME FROM `booking_services`, NOT FROM THIS FILE. The cards below are the one
 * place the original file's "do not list services" rule gets overridden — deliberately, since
 * the design leads with them — but they are rendered from the same rows the booking flow uses,
 * so they cannot drift from it. Nothing here is a hardcoded number.
 *
 * ⚠️ Every colour is `site.branding`. The booking flow sits on this same screen.
 */
/** Her page's sections, shared by the desktop nav and the phone menu so the two cannot disagree. */
const NAV = [['Services', 'services'], ['How it works', 'how'], ['Add-ons', 'extras'], ['Where I go', 'area']] as const;

export function LandingOneOffFluffyPaw({ site, landing, onAccent, onPickService }: TemplateProps) {
  const { accentColor, backgroundColor, textColor } = site.branding;
  const hero = landing.photos[0];
  const rest = landing.photos.slice(1, 3);

  /* Tints derived from her own colours, so the bands survive any palette she later picks. */
  const wash = `color-mix(in srgb, ${accentColor} 7%, ${backgroundColor})`;
  /* Sampled from the heart in her logo. Hardcoded, unlike everything else here, because this
     is her page and it is her second brand colour — there is nowhere in `branding` for it. */
  const babyBlue = '#C9EEF5';
  const edge = `color-mix(in srgb, ${textColor} 12%, transparent)`;

  /** The lowest figure on a card — the tier floor, or the flat price. */
  const fromCents = (s: Site['services'][number]) =>
    s.tiered && s.tiers.length ? Math.min(...s.tiers.map((t) => t.priceCents)) : s.priceCents;

  /* Add-ons are attached per service and mostly repeat across all of them; the page wants the
     distinct list once. */
  const addons = Array.from(
    new Map(site.services.flatMap((s) => s.addons).map((a) => [a.id, a])).values(),
  );
  const addonPrice = (a: (typeof addons)[number]) =>
    a.priceUnit === 'minute'
      ? `${formatCents(a.priceCents)}–${formatCents(a.priceMaxCents ?? a.priceCents)} / min`
      : a.priceMaxCents && a.priceMaxCents !== a.priceCents
        ? `${formatCents(a.priceCents)}–${formatCents(a.priceMaxCents)}`
        : formatCents(a.priceCents);

  /** "About 2 hours", "About 2½ hours", "About 30 minutes" — as the design writes it. */
  const duration = (mins: number) => {
    if (mins < 60) return `About ${mins} minutes`;
    const halves = Math.round(mins / 30) / 2;
    const whole = Math.floor(halves);
    const frac = halves - whole ? '½' : '';
    return `About ${whole}${frac} hour${halves === 1 ? '' : 's'}`;
  };

  /* The size range, in her own labels' first words: "XS through Giant", "Mini · Medium ·
     Standard". Three or fewer reads better listed than bracketed. */
  const sizeRange = (s2: Site['services'][number]) => {
    const heads = s2.tiers.map((t) => t.label.split(' ')[0]);
    if (heads.length === 0) return null;
    return heads.length <= 3 ? heads.join(' · ') : `${heads[0]} through ${heads[heads.length - 1]}`;
  };

  /*
   * Where she goes, taken from the "what city do you live in?" question she already maintains
   * rather than typed into this file — so the page cannot claim a town she has stopped
   * serving. The catch-all option is dropped, and the state suffix with it: this line is read
   * by people who live there.
   */
  const serviceArea = (site.settings.intakeQuestions.find(
    (q) => q.type === 'choice' && /city|town|area/i.test(q.label),
  )?.options ?? [])
    .filter((o) => !/somewhere else|other|elsewhere/i.test(o))
    .map((o) => o.replace(/,?\s+(SC|NC|GA)$/i, ''));

  /* The phone menu. Focus moves into it on open and back to the button on close, and Escape
     shuts it — the keyboard contract of any dialog. */
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const closeMenu = () => {
    setMenuOpen(false);
    menuButton.current?.focus({ preventScroll: true });
  };
  useEffect(() => {
    if (!menuOpen) return;
    closeButton.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const repeats = site.settings.recurringOptions.filter((r) => r.percentOff > 0);
  const everyN: Record<string, string> = {
    weekly: 'every week',
    biweekly: 'every 2 weeks',
    every_4_weeks: 'every 4 weeks',
    every_6_weeks: 'every 6 weeks',
    monthly: 'every month',
  };

  const Section = ({
    id,
    children,
    tinted = false,
    bg,
  }: { id?: string; children: React.ReactNode; tinted?: boolean; bg?: string }) => (
    <div
      id={id}
      className="scroll-mt-16"
      style={bg ? { backgroundColor: bg } : tinted ? { backgroundColor: wash } : undefined}
    >
      <div className="mx-auto max-w-6xl px-5 py-14 sm:py-20">{children}</div>
    </div>
  );

  /** Eyebrow + heading. Every band in the design carries both; they were missing entirely. */
  const Head = ({ eyebrow, children }: { eyebrow: string; children: React.ReactNode }) => (
    <div className="text-center wide:text-left">
      <p className="text-xs font-bold uppercase tracking-[0.16em]" style={{ color: accentColor }}>
        {eyebrow}
      </p>
      <h2 className="mx-auto mt-3 max-w-[14ch] text-4xl font-bold leading-[1.05] tracking-tight wide:mx-0 sm:text-5xl">
        {children}
      </h2>
    </div>
  );

  return (
    <div>
      {/*
        Masthead. Sticky, so the Book button is reachable from anywhere on a long page.
        `backdrop-blur` over a translucent background rather than a solid bar, so her photo
        does not get a hard band across it as it scrolls under.
      */}
      <header
        className="sticky top-0 z-20 backdrop-blur"
        style={{ backgroundColor: `color-mix(in srgb, ${backgroundColor} 85%, transparent)`, borderBottom: `1px solid ${edge}` }}
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-4">
          <span className="text-xl font-bold tracking-tight sm:text-2xl">{site.business.name}</span>
          <nav className="hidden gap-7 text-sm font-semibold opacity-80 wide-md:flex">
            {NAV.map(([label, id]) => (
              <a
                key={id}
                href={`#${id}`}
                onClick={(e) => scrollToId(e, id)}
                className="hover:opacity-100"
              >
                {label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <BookCta accent={accentColor} onAccent={onAccent} compact />
            <button
              ref={menuButton}
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              aria-expanded={menuOpen}
              aria-controls="fps-menu"
              className="grid h-10 w-10 place-items-center rounded-xl wide-md:hidden"
              style={{ border: `1px solid ${edge}` }}
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {/*
        The phone menu. Slides in from the right wherever the inline nav is hidden — below `md`,
        and on any phone held sideways (see `wide-md` in tailwind.config.js).

        ⚠️ A SIBLING OF THE HEADER, NOT A CHILD. The header has `backdrop-blur`, and any
        `backdrop-filter` makes an element the containing block for its `fixed` descendants —
        so a drawer inside it would be pinned to the 68px bar, not the screen.

        ⚠️ ALWAYS RENDERED, HIDDEN BY `invisible` WHEN SHUT, so the slide can animate both ways.
        `invisible` also takes the links out of the tab order and the accessibility tree; an
        off-screen panel that was merely translated away would still be tabbable.

        z-[60] so it covers Milo's bubble (z-40) and his open panel (z-50).
      */}
      <div
        className={`fixed inset-0 z-[60] wide-md:hidden ${menuOpen ? '' : 'invisible'}`}
        aria-hidden={!menuOpen}
      >
        <div
          onClick={closeMenu}
          /* touch-none: a drag on the dimmed page must not scroll the page under it. */
          className={`absolute inset-0 touch-none bg-black/40 transition-opacity duration-300 motion-reduce:transition-none ${
            menuOpen ? 'opacity-100' : 'opacity-0'
          }`}
        />
        <nav
          id="fps-menu"
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
          className={`absolute inset-y-0 right-0 flex w-72 max-w-[85vw] flex-col px-6 py-5 shadow-2xl transition-transform duration-300 motion-reduce:transition-none ${
            menuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          style={{ backgroundColor, color: textColor }}
        >
          <button
            ref={closeButton}
            type="button"
            onClick={closeMenu}
            aria-label="Close menu"
            className="grid h-10 w-10 place-items-center self-end rounded-xl"
            style={{ border: `1px solid ${edge}` }}
          >
            <X className="h-5 w-5" />
          </button>
          <ul className="mt-4">
            {NAV.map(([label, id]) => (
              <li key={id} style={{ borderBottom: `1px solid ${edge}` }}>
                <a
                  href={`#${id}`}
                  onClick={(e) => {
                    closeMenu();
                    scrollToId(e, id);
                  }}
                  className="block py-4 text-lg font-semibold"
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>
          <div className="mt-8" onClick={closeMenu}>
            <BookCta accent={accentColor} onAccent={onAccent} />
          </div>
        </nav>
      </div>

      {/*
        Hero. Logo beside the headline from `sm` up; stacked above it on a phone, either way up.

        ⚠️ THE STACK IS THE WHOLE POINT ON A PHONE, and it took a second pass to get here.
        Beside-the-headline at every width left the copy in a column about 190px wide on a
        375px screen — roughly twenty characters a line, so the two short paragraphs of
        subtext ran to eleven lines of ribbon with the CTA wrapping inside it, all against a
        column of dead space under the logo. Portrait has no width to give away.

        ⚠️ THE ORIGINAL NOTE HERE READ "no breakpoint — a media-queried grid is what dropped
        it under the button". That failure was a GRID whose columns collapsed, which put the
        logo after the CTA. This is a flex column reversed, so the logo lands above the
        eyebrow and the reading order is logo → tagline → headline → copy → button. Different
        mechanism, and the thing that note was guarding against does not happen.
      */}
      <div className="mx-auto max-w-6xl px-5 pt-8 sm:pt-12">
        <div className="flex flex-col-reverse items-center gap-6 text-center wide:flex-row wide:items-start wide:justify-between wide:gap-12 wide:text-left">
          <div className="min-w-0">
            {landing.tagline && (
              <p className="mb-4 text-sm font-bold uppercase tracking-[0.16em] opacity-60">{landing.tagline}</p>
            )}
            {/*
              ⚠️ THE HEADLINE IS SPLIT ON ITS LINE BREAK, and the second line takes the accent.
              That is how the approved design reads — "A spa day" in ink, "in your driveway."
              in her pink. Storing it as one field with a newline keeps it editable in the CRM
              rather than hardcoding her words into a component she cannot reach.
            */}
            <h1 className="text-5xl font-bold leading-[0.98] tracking-[-0.03em] sm:text-7xl lg:text-8xl">
              {(landing.headline?.trim() || site.business.name).split('\n').map((line, i) => (
                <span key={i} className="block" style={i > 0 ? { color: accentColor } : undefined}>
                  {line}
                </span>
              ))}
            </h1>
            {/*
              ⚠️ THE SUBTEXT AND CTA LIVE IN THIS COLUMN, not below the row. When they sat
              underneath, the headline was vertically centred against a 480px logo and the
              copy started below the logo's foot — which read as a large accidental gap under
              the heading rather than as spacing. Same column, and `mt-5` is then a real
              measurement instead of a guess about the logo's height.
            */}
            <div className="mx-auto mt-5 max-w-xl space-y-6 wide:mx-0">
              <About text={landing.about} />
              <BookCta accent={accentColor} onAccent={onAccent} />
            </div>
          </div>
          {site.branding.logoUrl && (
            <img
              src={`${API_URL}${site.branding.logoUrl}?v=${site.branding.logoVersion ?? ''}`}
              alt={site.business.name}
              /*
                A fixed 16rem on a phone, not a viewport fraction. `36vw` was sized to sit
                BESIDE the text; once it sits above it, the same rule makes a brand mark that
                grows with the screen while the space it has to fill does not. 16rem is about
                two-thirds of a 375px screen, centered over the headline; 10rem read as a stamp.
              */
              className="w-64 shrink-0 wide:w-[36vw] wide:max-w-[30rem] wide:self-start"
            />
          )}
        </div>
      </div>

      {hero && (
        <div className="mx-auto mt-10 max-w-6xl px-5">
          <Photo photo={hero} className="h-[40vh] max-h-[28rem] w-full rounded-2xl" />
        </div>
      )}

      <div className="mt-10 py-4" style={{ backgroundColor: wash }}>
        <ul className="mx-auto flex max-w-6xl flex-wrap justify-center gap-x-10 gap-y-2 px-5 text-sm font-semibold">
          {/*
            ⚠️ THE CHIP CHANGES WITH THE CHARGE, AUTOMATICALLY (#135). "Travel included" is
            true only while she charges nothing extra; the moment she sets an extended-area
            rate it becomes "Travel included locally", which is the wording Tristin approved.
            Deriving it from the setting is what makes the copy and the charge impossible to
            ship apart — the alternative was remembering to edit this line on the day, and
            forgetting means the page disclaims a fee it is charging.
          */}
          {[
            'One dog at a time',
            'No cages',
            'Back inside in about an hour',
            site.settings.travelUpchargePercent ? 'Travel included locally' : 'Travel included',
          ].map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
      </div>

      <Section id="services">
        <Head eyebrow="What I do">Pick what your dog needs.</Head>
        {site.settings.pricingNote && (
          <p className="mx-auto mt-3 max-w-2xl text-center opacity-75 wide:mx-0 wide:text-left">{site.settings.pricingNote}</p>
        )}
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {site.services.map((s) => {
            const from = fromCents(s);
            return (
              /*
                ⚠️ A REAL <button>, NOT A <div> WITH AN onClick. These already LOOKED like
                buttons — bordered, raised, one per service — so a customer was tapping them
                and getting nothing. Making the whole card the control is the fix, and it has
                to be a button element so it is reachable by keyboard and announced as
                something you can press. `text-left` because a button centres its text.
              */
              <div
                key={s.id}
                /*
                  ⚠️ A DIV WITH A CLICK, NOT A <button> WRAPPING THE WHOLE CARD. The obvious
                  version — make the card itself a button — is INVALID HTML: a button's
                  content model is phrasing content, and this card contains an <h3>. Browsers
                  render it, but it flattens the heading out of the accessibility tree, and
                  these headings are how a screen-reader user finds the service they want.

                  So the real control is the "Book this" button below, which is focusable,
                  announced and keyboard-operable. This click is a redundant convenience for
                  the large majority who tap the card — which they were already doing, and
                  getting nothing, because it looks exactly like a button.
                */
                onClick={() => onPickService?.(s.id)}
                className="flex cursor-pointer flex-col items-center gap-2 rounded-2xl p-5 text-center transition-transform wide:items-stretch wide:text-left hover:scale-[1.02] motion-reduce:transition-none motion-reduce:hover:scale-100"
                style={{ border: `1px solid ${edge}` }}
              >
                <h3 className="text-lg font-semibold">{s.name}</h3>
                {s.description && <p className="flex-1 text-sm opacity-75">{s.description}</p>}
                <p className="text-sm opacity-60">
                  {duration(s.durationMinutes)}
                  {s.tiered && s.tiers.length > 0 && ` · ${s.tiers.length} sizes`}
                </p>
                {/* A quote service shows no figure at all — never a zero. */}
                {from != null && (
                  <p className="text-3xl font-bold tracking-tight" style={{ color: accentColor }}>
                    {s.priceIsEstimate || s.tiered ? `From ${formatCents(from)}` : formatCents(from)}
                  </p>
                )}
                {sizeRange(s) && (
                  <p className="text-[0.68rem] font-bold uppercase tracking-[0.1em] opacity-55">{sizeRange(s)}</p>
                )}
                {/*
                  The real control — focusable and announced, so this card is reachable
                  without a mouse. `mt-auto` pins it to the bottom so the CTAs line up across
                  cards whose descriptions run to different lengths.

                  ⚠️ NO `focus:outline-none`. An earlier pass removed the native ring and
                  replaced it with a Tailwind one carrying no colour, which would have drawn
                  the framework's default blue on her page. The browser's own ring is always
                  visible, always sufficient, and costs nothing.
                */}
                <button
                  type="button"
                  onClick={(e) => {
                    // Both handlers do the same thing; without this the card's click also
                    // fires and the pick is registered twice.
                    e.stopPropagation();
                    onPickService?.(s.id);
                  }}
                  className="mt-auto inline-flex items-center gap-1.5 self-center rounded-xl px-4 py-2 text-sm font-semibold wide:self-start"
                  style={{ backgroundColor: accentColor, color: onAccent }}
                >
                  <CalendarCheck className="h-4 w-4" />
                  Book this
                </button>
              </div>
            );
          })}
        </div>
        {/*
          ⚠️ NO "Book an appointment" HERE ANY MORE. Every card now carries its own CTA, and
          a generic one underneath competes with four specific ones — the page's single-CTA
          rule (see the note in App.tsx) applied within a section rather than across the page.
        */}
      </Section>

      <Section id="how" bg={babyBlue}>
        <Head eyebrow="How it works">Three steps, and I come to you.</Head>
        <div className="mt-8 grid gap-8 sm:grid-cols-3">
          {[
            ['Book online', 'Pick the service, your dog’s size and a time that suits you.'],
            ['I park in your driveway', 'The van is the salon. You do not have to be home the whole time.'],
            ['Back inside, happy', 'Straight back through your own front door, no car ride either way.'],
          ].map(([title, body], i) => (
            <div key={title} className="text-center wide:text-left">
              <div
                className="mx-auto grid h-10 w-10 place-items-center rounded-full text-base font-bold wide:mx-0"
                style={{ backgroundColor: accentColor, color: onAccent }}
              >
                {i + 1}
              </div>
              <h3 className="mt-3 text-lg font-semibold">{title}</h3>
              <p className="mt-1 opacity-75">{body}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section>
        <div className="grid items-center gap-8 sm:grid-cols-2">
          <div className="text-center wide:text-left">
            <Head eyebrow="Why mobile">Kinder on a nervous dog.</Head>
            <p className="mt-3 opacity-75">
              A salon means a car ride, a strange room, other dogs barking, and hours in a crate
              waiting their turn. Here it is one dog, one groomer, and a familiar driveway.
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-2 wide:justify-start">
              {['Anxious dogs', 'Senior dogs', 'Multiple dogs', 'Big dogs', 'Busy families'].map((p) => (
                <span
                  key={p}
                  className="rounded-full px-3.5 py-1.5 text-sm font-semibold"
                  style={{ backgroundColor: wash, border: `1px solid ${edge}` }}
                >
                  {p}
                </span>
              ))}
            </div>
          </div>
          {rest.length > 0 ? (
            /*
             * The heights track the shape of a phone photo on purpose. Two of these sit side
             * by side in a half-width column, so each frame is ~161px across on a phone and
             * ~232px on a desktop; 224 and 320 tall put both within a few percent of the 3:4
             * a phone camera shoots in portrait. A single height that suited the desktop would
             * crop the sides off a dog on a phone, which is where most of this page is read.
             */
            <div className={`grid gap-3 ${rest.length > 1 ? 'grid-cols-2' : ''}`}>
              {rest.map((p) => (
                <Photo key={p.id} photo={p} className="h-56 w-full rounded-xl sm:h-80" />
              ))}
            </div>
          ) : (
            /*
             * ⚠️ A VISIBLE PLACEHOLDER ON A LIVE PAGE, which is a deliberate and TEMPORARY
             * state. It exists so the shape of the finished page is legible while she is
             * choosing photos. The moment two land in `landing.photos` it disappears on its
             * own — but if it is still here when real customers arrive, it reads as an
             * unfinished site, so it should not outlive the photo hunt.
             */
            <div className="grid grid-cols-2 gap-3">
              {['Before', 'After'].map((label) => (
                <div
                  key={label}
                  className="grid h-56 place-items-center rounded-xl text-xs font-bold uppercase tracking-widest opacity-50 sm:h-80"
                  style={{ border: `2px dashed ${edge}`, backgroundColor: wash }}
                >
                  {label}
                </div>
              ))}
            </div>
          )}
        </div>
      </Section>

      {addons.length > 0 && (
        <Section id="extras" tinted>
          <Head eyebrow="Add-ons">Anything else while I’m there.</Head>
          <div className="mt-6 grid gap-x-12 sm:grid-cols-2">
            {addons.map((a) => (
              <div
                key={a.id}
                className="flex items-baseline justify-between gap-4 py-3"
                style={{ borderBottom: `1px solid ${edge}` }}
              >
                <span>
                  {a.name}
                  {a.description && <span className="opacity-60"> · {a.description}</span>}
                </span>
                <span className="whitespace-nowrap font-semibold tabular-nums">{addonPrice(a)}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {repeats.length > 0 && (
        <Section>
          <div className="rounded-3xl px-6 py-10 text-center" style={{ backgroundColor: wash }}>
            <p className="text-xs font-bold uppercase tracking-[0.16em]" style={{ color: accentColor }}>Regulars</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">Book on a cycle, pay less.</h2>
            <div className="mt-6 flex flex-wrap justify-center gap-x-12 gap-y-4">
              {repeats.map((r) => (
                <div key={r.recurrence}>
                  <div className="text-3xl font-bold tracking-tight" style={{ color: accentColor }}>
                    {r.percentOff}% off
                  </div>
                  <div className="text-sm font-semibold opacity-70">{everyN[r.recurrence] ?? r.recurrence}</div>
                </div>
              ))}
            </div>
          </div>
        </Section>
      )}

      {/*
        Social proof. Her own quotes when she has them; otherwise obviously-unfinished cards,
        the same bargain as the before/after slots — the wording is plainly a placeholder so a
        visitor cannot mistake it for a testimonial, and the whole block is replaced the moment
        real ones exist.
      */}
      <Section id="reviews" tinted>
        <Head eyebrow="Kind words">What my customers say.</Head>
        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {landing.reviews.length > 0
            ? landing.reviews.map((r) => (
                <figure key={r.id} className="rounded-2xl p-5 text-center wide:text-left" style={{ backgroundColor, border: `1px solid ${edge}` }}>
                  {/* A star only where she recorded one — never a defaulted five. */}
                  {r.rating != null && (
                    <div className="flex justify-center gap-0.5 wide:justify-start" style={{ color: accentColor }}>
                      {Array.from({ length: r.rating }, (_, i) => (
                        <Star key={i} className="h-4 w-4 fill-current" />
                      ))}
                    </div>
                  )}
                  <blockquote className="mt-2">{r.quote}</blockquote>
                  {(r.author || r.source) && (
                    <figcaption className="mt-3 text-sm font-semibold opacity-70">
                      {[r.author, r.source].filter(Boolean).join(' · ')}
                    </figcaption>
                  )}
                </figure>
              ))
            : [
                ['Real review goes here — three or four lines is plenty.', serviceArea[0]],
                ['Real review goes here.', serviceArea[1]],
              ].map(([quote, where], i) => (
                <figure key={i} className="rounded-2xl p-5 text-center wide:text-left" style={{ backgroundColor, border: `1px solid ${edge}` }}>
                  <div className="flex justify-center gap-0.5 wide:justify-start" style={{ color: accentColor }}>
                    {Array.from({ length: 5 }, (_, j) => (
                      <Star key={j} className="h-4 w-4 fill-current" />
                    ))}
                  </div>
                  <blockquote className="mt-2">{quote}</blockquote>
                  <figcaption className="mt-3 text-sm font-semibold opacity-70">
                    First name, last initial{where ? ` · ${where}` : ''}
                  </figcaption>
                </figure>
              ))}
          {landing.reviews.length === 0 && (
            <div
              className="grid place-items-center rounded-2xl p-5 text-center text-sm opacity-60"
              style={{ border: `2px dashed ${edge}` }}
            >
              Needs 3–4 real quotes. Ask customers she has already groomed — first name and
              initial is enough.
            </div>
          )}
        </div>
        {/*
          The ask, put where a visitor has just finished reading somebody else's. Drawn only
          when she has typed a link in her CRM — a "Leave us a review" that goes nowhere is
          worse than no ask at all.
        */}
        <div className="flex flex-col items-center wide:items-start">
          {landing.reviewUrl && (
            <a
              href={landing.reviewUrl}
              target="_blank"
              /* noopener: her own link, to a site we do not control — the same terms Social uses. */
              rel="noreferrer noopener"
              className="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold underline underline-offset-4 opacity-80 transition-opacity hover:opacity-100"
              style={{ color: accentColor }}
            >
              <Star className="h-4 w-4 fill-current" />
              Leave us a review
            </a>
          )}
          <Social links={landing.social} />
        </div>
      </Section>

      {/* Closing block. This is what "Where I go" points at, and the last thing before the
          booking flow itself.

          ⚠️ NO CTA HERE, DELIBERATELY. It had one until 2026-09-12, and it sat directly on top
          of its own target: a "Book an appointment" button scrolling a couple of hundred pixels
          to a section already headed "Book an appointment", with the form visible below it on
          most screens. A button that scrolls to something you can already see reads as broken.

          The band itself stays because it is load-bearing — `id="area"` is what the "Where I go"
          nav link points at, and the service areas and pricing note are the last thing a visitor
          needs before the form. See the ONE CTA note in App.tsx. */}
      <div id="area" className="mx-auto max-w-6xl scroll-mt-16 px-5 py-14 sm:py-20">
        <div className="rounded-3xl px-6 py-14 text-center" style={{ backgroundColor: accentColor, color: onAccent }}>
          <h2 className="text-4xl font-bold tracking-tight sm:text-5xl">Ready when your dog is.</h2>
          {(serviceArea.length > 0 || site.settings.pricingNote) && (
            <p className="mx-auto mt-4 max-w-2xl opacity-90">
              {serviceArea.join(' · ')}
              {serviceArea.length > 0 && site.settings.pricingNote ? '. ' : ''}
              {site.settings.pricingNote}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export const TEMPLATES = {
  hero: LandingHero,
  split: LandingSplit,
  gallery: LandingGallery,
} as const;
